// @flow

import type { ImageSize } from "types";
import { get_max_rect_to_fit, get_ratio } from "utils/size_utils";

declare var pageYOffset: number;

export function invert_when(n: number, cond: boolean) {
  return cond ? 1 / n : n;
}

/**
 * If `condition` is false, log the state.
 * @param condition
 * @param message
 */
export function assert(condition: boolean, message: string) {
  console.assert(condition, message); // eslint-disable-line no-console
}

/*
 * Whether the DOM element is about to be visible within the next
 * page from the current viewport.
 */
export function is_elem_about_to_be_visible(element: HTMLElement) {
  const element_top = element.getBoundingClientRect().top;
  return pageYOffset + window.innerHeight > element_top;
}

/*
 * Whether the DOM element is hidden (via "display: none")
 * https://stackoverflow.com/a/21696585/128601
 */
export function is_elem_hidden(element: HTMLElement) {
  return element.offsetParent === null;
}
/**
 * Get browser's current path, query and hash without the host
 */
export function get_url(): string {
  return `${location.pathname}${location.search}`;
}

/**
 * Get the browser's current host with protocol and port.
 */
export function get_host(): string {
  return `${location.protocol}//${location.host}`;
}

/**
 * Get the top-level container's size if we can find one.
 * Otherwise, get the root <div> size.
 */
export function get_viewport_dimension(): ImageSize {
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * Get the container size given the viewport size and
 * how much margin we want to keep.
 */
export function get_container_dimension(
  print_size: string,
  is_portrait: boolean,
  required_fixed_orientation: boolean,
  tolerance: number
): [number, number] {
  let ratio = get_ratio(print_size);
  let {
    width: viewport_width,
    height: viewport_height
  } = get_viewport_dimension();
  let { width, height } = get_max_rect_to_fit(
    viewport_width * tolerance,
    viewport_height * tolerance,
    is_portrait || required_fixed_orientation ? ratio : 1 / ratio
  );
  return [width, height];
}

/** Redirect to another url. Make things easier to test as well as a single
 *  place to refactor this page redirect behavior later.
 */
export function redirect(url: string) {
  window.location.href = url;
}

export function reload() {
  window.location.reload();
}

export function open_in_new_tab(url: string) {
  window.open(url, "_blank");
}
